<template>
    <v-container fluid class="mx-0 px-0">
        <v-row>
            <v-col cols="12">
                <template>
                    <v-data-table
                        :headers="tableHeaders"
                        :items="stats"
                        class="stats-table no-global-table-hover elevation-1 rounded-lg"
                        hide-default-footer
                        disable-sort
                        :mobile-breakpoint="0"
                        :items-per-page="-1"
                        @click:row="goToMatch"
                    >
                        <!-- every foul of the match: personal, technical and unsportsmanlike -->
                        <template v-slot:item.fouls="{ item }">
                            {{ foulsOf(item) }}
                        </template>
                        <template v-slot:header.ts_percentage="{ header }">
                            <div class="d-inline-flex align-center">
                                <span>{{ header.text }}</span>
                                <info-tip bottom max-width="380" content-class="ts-tooltip-surface">
                                    <v-icon
                                        small
                                        class="ml-1 ts-info-icon"
                                        color="orange"
                                    >
                                        mdi-information-outline
                                    </v-icon>
                                    <template #content>
                                        <div class="ts-tooltip">
                                            <div class="font-weight-bold mb-1">True Shooting Percentage (TS%)</div>
                                            <div class="mb-2">TS% = Points / (2 x (Field Goal Attempts + 0.44 x Free Throw Attempts))</div>
                                            <div class="ts-poor">Below 50%: Poor scoring efficiency</div>
                                            <div class="ts-below-average">50% to below 55%: Below average</div>
                                            <div class="ts-average">55%: Solid and league average</div>
                                            <div class="ts-very-good">57% to 58%: Very good / highly efficient</div>
                                            <div class="ts-elite">60% and above: Elite</div>
                                        </div>
                                    </template>
                                </info-tip>
                            </div>
                        </template>
                        <template v-slot:item.playerTeam="{ item }">
                            <div class="game-team">
                                <img v-if="item.teamLogo" :src="`${assetsUrl}/${item.teamLogo}`" height="24px" alt="">
                                <div>
                                    <div class="font-weight-bold">{{ item.teamName }}</div>
                                    <div v-if="item.seasonName" class="game-team__season">{{ item.seasonName }}</div>
                                </div>
                            </div>
                        </template>
                        <template v-slot:item.team="{ item }">
                            <v-row dense align="center">
                                <v-col cols="4" align="start">
                                    <img
                                        :src="`${assetsUrl}/${item.opponentTeamLogo}`"
                                        height="30px"
                                        contain
                                    >
                                </v-col>
                                <v-col cols="8" align="start">
                                    <span class="font-weight-bold">{{ item.opponentTeamName }}</span>
                                </v-col>
                            </v-row>
                        </template>
                        <template v-slot:item.free_throw_percentage="{ item }">
                            <v-row dense align="center">
                                <v-col cols="12" align="start">
                                    {{ item.freeThrowAttempts > 0 ? ((item.freeThrowsMade / item.freeThrowAttempts) * 100).toFixed(1) : 0 }} {{ `% (${item.freeThrowsMade} / ${item.freeThrowAttempts})` }}
                                </v-col>
                            </v-row>
                        </template>
                        <template v-slot:item.ts_percentage="{ item }">
                            <v-row dense align="center">
                                <v-col cols="12" align="start">
                                    <span :class="['font-weight-bold', tsClass(tsPct(item))]">
                                        {{ tsPct(item) }}%
                                    </span>
                                </v-col>
                            </v-row>
                        </template>
                        <template v-slot:item.two_points_percentage="{ item }">
                            <v-row dense align="center">
                                <v-col cols="12" align="start">
                                    {{ item.twoPointAttempts ? ((item.twoPointsMade / item.twoPointAttempts) * 100).toFixed(1) : 0 }} {{ `% (${item.twoPointsMade} / ${item.twoPointAttempts})` }}
                                </v-col>
                            </v-row>
                        </template>
                        <template v-slot:item.three_points_percentage="{ item }">
                            <v-row dense align="center">
                                <v-col cols="12" align="start">
                                    {{ item.threePointAttempts ? ((item.threePointsMade / item.threePointAttempts) * 100).toFixed(1) : 0 }} {{ `% (${item.threePointsMade} / ${item.threePointAttempts})` }}
                                </v-col>
                            </v-row>
                        </template>
                        <template v-slot:item.playTimeInSecs="{ item }">
                            <span>
                                {{ secToMinutesSeconds(item.playTimeInSecs) }}
                            </span>
                        </template>
                        <template v-slot:item.round="{ item }">
                            <span>
                                {{ item.customRoundText || item.round }}
                            </span>
                        </template>
                    </v-data-table>
                </template>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import { foulsOf } from '@/utils/playerStats'

    export default {
        props: {
            stats: {
                type: Array,
                default: null
            },
            // the team (and season) of the player in each match: a free agent has played for several
            showTeam: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                headers: [
                    { text: 'Αντίπαλος', value: 'team', width: '170px', align: 'start' },
                    {
                        text: 'Αγωνιστική',
                        value: 'round',
                        align: 'center'
                    },
                    { text: 'Pts', value: 'points', align: 'center' },
                    { text: '2P%', value: 'two_points_percentage', width: '150px', align: 'center' },
                    { text: '3P%', value: 'three_points_percentage', width: '150px', align: 'center' },
                    { text: 'FT%', value: 'free_throw_percentage', width: '150px', align: 'center' },
                    { text: 'TS%', value: 'ts_percentage', width: '100px', align: 'center' },
                    { text: 'OR', value: 'offensiveRebounds', align: 'center' },
                    { text: 'DR', value: 'defensiveRebounds', align: 'center' },
                    { text: 'TR', value: 'rebounds', align: 'center' },
                    { text: 'AST', value: 'assists', align: 'center' },
                    { text: 'STL', value: 'steals', align: 'center' },
                    { text: 'TO', value: 'turnovers', align: 'center' },
                    { text: 'BLK', value: 'blockedShots', align: 'center' },
                    { text: 'FC', value: 'fouls', align: 'center' },
                    { text: 'PIR', value: 'pir', align: 'center' },
                    { text: 'Min', value: 'playTimeInSecs', align: 'center', width: '80px' }
                ]
            }
        },
        computed: {
            tableHeaders() {
                return this.showTeam
                    ? [{ text: 'Ομάδα', value: 'playerTeam', width: '170px', align: 'start' }].concat(this.headers)
                    : this.headers
            }
        },
        methods: {
            foulsOf,
            getMatchId(item) {
                return item.id || item.matchId || (item.match ? item.match.id : null)
            },
            goToMatch(item) {
                const matchId = this.getMatchId(item)
                if (!matchId) {
                    return
                }
                this.$router.push({ name: 'Match', params: { id: matchId } })
            },
            secToMinutesSeconds(secs) {
                if (secs < 0) return '-'
                if (typeof secs !== 'number' || isNaN(secs)) return '-'
                const minutes = Math.floor(secs / 60)
                const seconds = Math.floor(secs % 60)
                if (isNaN(minutes) || isNaN(seconds)) return '-'
                return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
            },
            tsPct(item = {}) {
                const points = Number(item.points) || 0
                const fga = (Number(item.twoPointAttempts) || 0) + (Number(item.threePointAttempts) || 0)
                const fta = Number(item.freeThrowAttempts) || 0
                const denominator = 2 * (fga + (0.44 * fta))
                if (denominator === 0) return (0).toFixed(1)
                return ((points / denominator) * 100).toFixed(1)
            },
            tsClass(tsValue) {
                const ts = Number(tsValue)
                if (!Number.isFinite(ts)) return 'ts-poor'
                if (ts < 50) return 'ts-poor'
                if (ts < 55) return 'ts-below-average'
                if (ts < 57) return 'ts-average'
                if (ts < 60) return 'ts-very-good'
                return 'ts-elite'
            }
        }
    }
</script>

<style scoped>
.game-team {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
}

.game-team__season {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.72rem;
}

/* ---------- Same look as the standings table ---------- */
.stats-table {
    overflow: hidden;
    font-family: Ubuntu, Arial, sans-serif;
}

.stats-table ::v-deep .v-data-table__wrapper table > thead > tr > th {
    background: #0d0d0f !important;
    color: #fff !important;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    border-bottom: 3px solid #ff7133 !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > thead > tr > th .v-icon {
    color: #fff !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr > td {
    height: 52px;
    white-space: nowrap;
    background: #fff;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr:nth-child(even) > td {
    background: #f7f7f8 !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td {
    background: #fff3ec !important;
}

.stats-table ::v-deep .v-data-table__wrapper table > tbody > tr:hover > td:first-child {
    box-shadow: inset 4px 0 0 #ff7133;
}

.stats-table ::v-deep tbody tr {
    cursor: pointer;
}

.ts-info-icon {
    cursor: pointer;
}

.ts-tooltip {
    font-size: 13px;
    line-height: 1.4;
}

::v-deep .v-tooltip__content.ts-tooltip-surface {
    background: #fff !important;
    opacity: 1 !important;
    color: #111;
    border: 1px solid #d7dbe3;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.ts-poor {
    color: #d32f2f;
}

.ts-below-average {
    color: #ef6c00;
}

.ts-average {
    color: #f9a825;
}

.ts-very-good {
    color: #2e7d32;
}

.ts-elite {
    color: #1b5e20;
}
</style>
